import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Logger from '../../common/logger';
import CustomButton from '../../components/CustomButton';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import CustomModal from './CustomModal';
import {
    productRetireOutcome,
    retireFailureFrom,
} from '../services/productRetireOutcome';

/** Where a delete's texts live — one block per kind of thing deleted. */
export type HardDeleteI18nPrefix =
    | 'services.products.delete'
    | 'services.product_categories.delete'
    | 'asset_detail.delete'
    | 'issue_reports.delete'
    | 'station_detail.delete';

interface Props {
    /** What is being deleted, as the operator knows it — shown in every text. */
    name: string;
    /**
     * Keys under it: `confirm_title`, `confirm_body`, `confirm_cta`, `cancel`,
     * `success`, `failed_title`, `error_forbidden`, `error_stale`,
     * `error_service`, `error_blocked`, and `blocker_<reason>` per refusal.
     * `hardDeleteKeys.test.ts` pins them in both locales.
     */
    i18nPrefix: HardDeleteI18nPrefix;
    /** Runs the delete; rejects with the RTK Query error on failure. */
    run: () => Promise<unknown>;
    onClose: () => void;
    /** After a successful delete — navigate away, clear a selection. */
    onDeleted?: () => void;
}

/**
 * Confirm and run a master-only hard delete (#2636) — products, product
 * categories, objects and problem reports share it.
 *
 * The delete cannot be undone, so the dialog names the thing and says so, and
 * the confirm button carries the destructive verb rather than "OK".
 *
 * Like `ProductRetireDialog` (whose classifier it reuses), it stays open on a
 * refusal and lists the reasons the server named: "this was booked, retire it
 * instead" is the expected answer for most rows, and an operator who only
 * sees "could not be deleted" learns nothing. A 404 says the thing no longer
 * exists — the stale-tab case — rather than closing as if it had worked.
 */
const HardDeleteDialog = ({ name, i18nPrefix, run, onClose, onDeleted }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [reasonKeys, setReasonKeys] = useState<string[]>([]);
    const [detail, setDetail] = useState<string | null>(null);

    const handleDelete = async () => {
        setReasonKeys([]);
        setDetail(null);
        setIsLoading(true);
        try {
            await run();
            dispatch(enqueueSuccessSnackbar({
                message: t(`${i18nPrefix}.success`, { name }),
                autoHideDuration: 4000,
            }));
            onClose();
            onDeleted?.();
        } catch (err) {
            Logger('HardDeleteDialog').error(`Hard delete failed: ${JSON.stringify(err)}`);
            const outcome = productRetireOutcome(retireFailureFrom(err));
            setReasonKeys(outcome.reasonKeys);
            setDetail(outcome.detail ?? null);
        } finally {
            setIsLoading(false);
        }
    };

    const hasFailure = reasonKeys.length > 0 || !!detail;

    return (
        <CustomModal
            open={true}
            title={t(`${i18nPrefix}.confirm_title`)}
            description={name}
            onClose={onClose}
            closeButton={
                <CustomButton
                    title={t(`${i18nPrefix}.cancel`)}
                    isFilled={false}
                    onClick={onClose}
                />
            }
            saveButton={
                <CustomButton
                    title={t(`${i18nPrefix}.confirm_cta`)}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleDelete}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                <p className={'text-sm text-foreground/70'}>
                    {t(`${i18nPrefix}.confirm_body`, { name })}
                </p>

                {hasFailure && (
                    <div
                        className={'rounded-[5px] border border-foreground/15 bg-foreground/5'
                            + ' px-4 py-3 text-sm text-foreground/70'}
                        role={'alert'}
                    >
                        <p className={'font-medium text-foreground mb-1'}>
                            {t(`${i18nPrefix}.failed_title`, { name })}
                        </p>
                        {reasonKeys.length > 0 && (
                            <ul className={'list-disc pl-5 flex flex-col gap-1'}>
                                {reasonKeys.map((key) => (
                                    <li key={key}>{t(`${i18nPrefix}.${key}`)}</li>
                                ))}
                            </ul>
                        )}
                        {detail && <p>{detail}</p>}
                    </div>
                )}
            </div>
        </CustomModal>
    );
};

export default HardDeleteDialog;
